import React from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';
import AutoComplete from '../../components/FormFields/Autocomplete';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as ProfileActions from '../../actions/profileActions';
import * as UiActions from '../../actions/uiActions';


import classes from './CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(45)
        .required(),
    basType: Yup.string()
        .required()
});

const CreateProfileModal = ({
    basTypes,
    close,
    clearErrors,
    fetchProfiles,
    open,
    title,
    isLoading,
    toggleLoading,
    errors,
    ...props
}) => {
    const formik = useFormik({
        initialValues: {
            name:    '',
            basType: ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                companyInfo,
                createProfile,
                enqueueSnackbar
            } = props;

            const body = {
                name:    formik.values.name,
                basType: formik.values.basType.name
            };

            let isCreated;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createProfile(companyInfo.companyId, body);
                }

                if (isCreated) {
                    handleClose();
                    await fetchProfiles(companyInfo.companyId);

                    enqueueSnackbar('Profile created successfully', 'success');
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    return (
        <EsmsDialog
            onClose={close}
            disabled={formik.isSubmitting}
            onConfirm={formik.handleSubmit}
            confirmText="Create"
            open={open}
            isLoading={isLoading}
            title={title}
            cancelable={true}
            errors={errors}
            onRendered={formik.handleReset}
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/profilesIconColor.svg" alt="Roles Icon" />
                        <span>Create Profile</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Provide a Profile Name.
                        </p>
                        <p>
                            Select a BAS Type for your profile.
                        </p>
                        <p>
                            Note: Profiles can only applied to Devices of the same BAS Type.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.fullWidth}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <AutoComplete
                            grouped
                            label="BAS Type"
                            name="basType"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            options={basTypes}
                            placeholder="BAS Type"
                            value={formik.values.basType}
                            error={Boolean(formik.errors.basType) && Boolean(formik.touched.basType)}
                            helperText={Boolean(formik.touched.basType) && formik.errors.basType}
                        />
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateProfileModal.defaultProps = {
    clearErrors: () => { },
    close:       () => { },
    companyInfo: {},
    create:      () => { },
    errors:      [],
    isLoading:   false,
    open:        false,
    title:       ''
};

CreateProfileModal.propTypes = {
    clearErrors: func,
    close:       func,
    companyInfo: object,
    create:      func,
    errors:      array,
    isLoading:   bool,
    open:        bool,
    title:       string
};

const mapStateToProps = state => {
    return {
        basTypes:    state.profile.basTypes,
        companyInfo: state.companyInfo,
        errors:      state.messages.errors,
        isLoading:   state.ui.isLoading
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...ProfileActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateProfileModal);
